/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Target } from '@fluentui/react/lib/Callout';
import { Stack } from '@fluentui/react/lib/Stack';
import { ITextFieldStyles } from '@fluentui/react/lib/TextField';
import { getId } from '@fluentui/react/lib/Utilities';
import { transaction } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import { LuisTextField } from 'src/core/components/LuisTextField';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getEventLogger } from 'src/core/services/telemetry/logEventBuilder';
import { getScopedTheme, getStylist } from 'src/core/themes/luisStylist';
import { mapUserRoleToUserActions } from 'src/core/utils/azureUtils';
import { noop } from 'src/core/utils/base';
import { LuisKeyCodes } from 'src/core/utils/keyCodes';
import { tokenToCharIndices } from 'src/core/utils/labelingUtils';
import { SidePaneEntitySelection } from 'src/luis/appShell/buildPane/intentPage/stores/SidePaneEntitySelectionStore';
import { UtteranceObservable } from 'src/luis/appShell/buildPane/intentPage/types';
import { Intent } from 'src/luis/modules/intents/Intent';
import { inBetween } from 'src/luis/modules/labeling/utils/generalUtils';
import { createTeachingBubble, dequeueTeachingBubble } from 'src/luis/projects/events/utils/teachingBubbles';
import { UtteranceRowOptions } from 'src/luis/projects/utterances/components/UtteranceTable/UtteranceRowOptions';
import { useUtteranceActions } from 'src/luis/projects/utterances/hooks/useUtteranceActions';
import { LabelingStore } from 'src/luis/projects/utterances/stores/LabelingStore';
import { UtteranceTableViewOptions } from 'src/luis/projects/utterances/types/UtteranceTableViewOptions';
import { utterancesTeachingBubbleMessages } from 'src/luis/projects/utterances/utils/utterancesTeachingBubbleMessages';
import { Model } from 'src/luis/types/Model';
import { LuisId } from 'src/luis/types/Resource';
import { Labeler, LabelerProps, LabelerRef } from 'src/modules/labeling/components/Labeler';
import { Label } from 'src/modules/labeling/types/Label';
import { Selection } from 'src/modules/labeling/types/Selection';
import { INVALID_INDEX } from 'src/modules/labeling/utils/labelingReducer';

export type IntentPageUtteranceRowProps = {
    /**
     * The model having this utterance.
     */
    model: Omit<Model, 'name'>;

    /**
     * The labeling store that encapsulate all the UI states.
     */
    labelingStore: LabelingStore;

    /**
     * The entities selected from the entity palette.
     */
    sidePaneEntitySelection: SidePaneEntitySelection;

    /**
     * Intents passed to the contextual menu.
     */
    intents: Intent[];

    /**
     * Utterance tokenized text as segments.
     */
    utteranceObservable: UtteranceObservable;

    /**
     * Id of the utterance being edited.
     */
    utteranceIdBeingEdited: LuisId;

    /**
     * The last training date for the app.
     */
    lastTrainingDate: Date;

    /*
     * Indicates whether the add as pattern in contextual mene disabled or not
     */
    isAddAsPatternDisabled: boolean;

    /**
     * Callback function fired when the utterance edit mode changed
     */
    onEdit: (selectedUtteranceId: LuisId) => void;

    /**
     * Callback function fired when the utterance edit submitted
     */
    onSubmitEditInput: (text: string) => void;

    /**
     * Callback function fired when the utterance edit mode dismissed
     */
    onDismissEditInput: () => void;

    /**
     * Callback function for the delete action
     */
    onDelete: (selectedUtteranceId: LuisId) => void;

    /**
     * Callback function for reassign intent action for selected utterances
     */
    onReassignIntent: (intentId: string, selectedUtteranceId: LuisId) => void;

    /**
     * Callback function for confirm the utterance entity predictions
     */
    onConfirmPrediction: (selectedUtteranceId: LuisId) => void;

    /**
     * Callback function for add new utterance as pattern action
     */
    onAddToPatterns: () => void;

    /**
     * Wether the utterance's culture is RTL or not.
     */
    isRTL: boolean;

    /**
     * Utterance view options
     */
    viewOptions: UtteranceTableViewOptions;

    /**
     * User Role to extract user actions.
     */
    userRole: string;
};

const { styleComponent } = getStylist('NewUtteranceRow');
const utteranceRowTheme = getScopedTheme('NewUtteranceRow');

const StyledLabeler = styleComponent<LabelerProps & React.RefAttributes<LabelerRef>>(Labeler)('StyledLabeler', theme => ({
    margin: theme.labelerMargin
}));

const baseInputStyling = {
    minHeight: utteranceRowTheme.editUtteranceInputHeight
};
const editUtteranceInputStyles = (): Partial<ITextFieldStyles> => ({
    ...{
        fieldGroup: {
            backgroundColor: utteranceRowTheme.editInputBackgroundColor
        },
        root: baseInputStyling
    }
});

const getSelectedText = (text: string, tokenizedText: string[], startTokenIndex: number, endTokenIndex: number) => {
    const charIndices = tokenToCharIndices({
        text,
        tokenizedText,
        startTokenIndex,
        endTokenIndex
    });

    return text.substring(charIndices.startCharIndex, charIndices.endCharIndex + 1);
};

// A generated class attached to the entity dropdown menu to be used for keyboard accessibility.
export const entityDropDownFocusId = getId();

/**
 * The view of the first cell in the utterances table that contain:
 * 1. The utterance text itself or the editable input field if the edit mode is enabled.
 * 2. The contextual menu of the specified utterance to support additional actions to be applied to the selected utterance.
 */
export const IntentPageUtteranceRow = observer((props: IntentPageUtteranceRowProps) => {
    const {
        utteranceObservable,
        lastTrainingDate,
        intents,
        onDelete,
        onAddToPatterns,
        utteranceIdBeingEdited,
        onReassignIntent,
        onEdit,
        onSubmitEditInput,
        onDismissEditInput,
        onConfirmPrediction,
        isRTL,
        viewOptions,
        isAddAsPatternDisabled,
        model,
        labelingStore,
        sidePaneEntitySelection,
        userRole
    } = props;
    const [isHovering, setIsHovering] = React.useState(false);
    const labelerRef = React.useRef<LabelerRef>();
    const { localize } = useLocalization();
    const { uiStore } = useGlobalState();
    const eventLoggerRef = React.useRef(getEventLogger('NewUtteranceRowBase'));

    const { brushStore, menuStore } = labelingStore;

    const [utterance, { labels, conflicts }] = utteranceObservable.computed.get();

    const { addLabel, addSynonymToSublist } = useUtteranceActions({ model });
    const [utteranceInput, setUtteranceInput] = React.useState(utterance.text);

    const isBrushEnabled = labelingStore.mode === 'brush';
    const isRowDisabled = labelingStore.disabledUtteranceId === utterance.id;
    const isUntrainedUtterance = !lastTrainingDate || utterance.modifiedDate > lastTrainingDate;
    const isEditMode = utteranceIdBeingEdited === utterance.id;

    const { canWrite } = mapUserRoleToUserActions(userRole);

    const labelerConfig = React.useMemo(
        () => ({
            isRTL,
            disabled: isRowDisabled || !canWrite,
            viewOptions: { hideNames: viewOptions.hideEntityNames, isBold: isUntrainedUtterance },
            disableHoverBrackets: isBrushEnabled
        }),
        [isRTL, isRowDisabled, isBrushEnabled, isUntrainedUtterance, viewOptions.hideEntityNames]
    );

    React.useEffect(() => () => dequeueTeachingBubble('NewPredictionsBehavior'), []);

    React.useEffect(() => {
        const predictedLabels = labels.some(label => label.renderedAs === 'box');

        if (predictedLabels && !uiStore.teachingBubbles.NewPredictionsBehavior) {
            createTeachingBubble(
                'NewPredictionsBehavior',
                localize(utterancesTeachingBubbleMessages.newPredictionsBehaviorBody),
                localize(utterancesTeachingBubbleMessages.newPredictionsBehaviorTitle),
                labelerRef.current.htmlElement,
                true
            );
        }
    }, [labels, Object.values(uiStore.teachingBubbles)]);

    const handleSelect = React.useCallback(
        (selection?: Selection, target?: Target) => {
            if (!selection || selection[0] === INVALID_INDEX) {
                return;
            }

            transaction(() => {
                const selectedText = getSelectedText(utterance.text, utterance.tokenizedText, selection[0], selection[1]);
                labelingStore.setTokenSelectionMetadata({
                    selection: selection as Selection,
                    utteranceId: utterance.id,
                    selectedText
                });

                menuStore.setCancelSelectionCallBackCB(() => labelerRef.current.cancelSelection());

                if (labelingStore.mode === 'menu') {
                    if (utterance.id !== menuStore.currentUtteranceId) {
                        menuStore.setCurrentUtteranceId(utterance.id);
                    }

                    menuStore.setTarget(target);

                    return;
                }

                if (!sidePaneEntitySelection.roleId && sidePaneEntitySelection.normalizedValue) {
                    addSynonymToSublist(
                        sidePaneEntitySelection.entityId,
                        sidePaneEntitySelection.normalizedValue,
                        utterance.tokenizedText.slice(selection[0], selection[1] + 1).join(' ')
                    );
                } else if (sidePaneEntitySelection.entityId) {
                    addLabel(
                        utterance.id,
                        {
                            startTokenIndex: selection[0],
                            endTokenIndex: selection[1],
                            entityId: sidePaneEntitySelection.entityId,
                            roleId: sidePaneEntitySelection.roleId && sidePaneEntitySelection.roleId
                        },
                        'brush'
                    );
                }
                menuStore.resetMenuState();
                labelerRef.current.cancelSelection();
            });
        },
        [labelingStore, labelerRef, menuStore, labels, utterance, brushStore, sidePaneEntitySelection]
    );

    const handleShapeClick = React.useCallback(
        (label: Label, target: Target) => {
            transaction(() => {
                if (utterance.id !== menuStore.currentUtteranceId) {
                    menuStore.setCurrentUtteranceId(utterance.id);
                }

                labelingStore.setTokenSelectionMetadata({
                    selection: [label.start, label.end],
                    utteranceId: utterance.id,
                    selectedText: getSelectedText(utterance.text, utterance.tokenizedText, label.start, label.end)
                });
                menuStore.setTarget(target);
                menuStore.setClickedOnLabel(label);
                menuStore.setCancelSelectionCallBackCB(() => labelerRef.current.cancelSelection());
            });
        },
        [labelingStore, menuStore, labels]
    );

    const handleTokenHover = React.useCallback(
        (tokenIndex: number) => {
            transaction(() => {
                if (labelingStore.mode !== 'brush') {
                    return;
                }

                if (brushStore.currentUtteranceId !== utterance.id) {
                    brushStore.setCurrentUtteranceId(utterance.id);
                }

                const hoveredEntities = labels
                    .filter(({ start, end, renderedAs }) => inBetween(start, end)(tokenIndex) && renderedAs === 'line')
                    .map(({ entity }) => entity);

                if (!hoveredEntities.length && !brushStore.hoveredEntitiesIds.length) {
                    return;
                }

                brushStore.setHoveredEntitiesIds(hoveredEntities.map(e => e.id as string));
            });
        },
        [brushStore, labels]
    );

    const handleTokenHoverExit = React.useCallback(() => {
        transaction(() => {
            brushStore.setCurrentUtteranceId(null);

            if (brushStore.hoveredEntitiesIds.length) {
                brushStore.setHoveredEntitiesIds([]);
            }
        });
    }, [brushStore]);

    const handleKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
        if (e.key === LuisKeyCodes.Tab) {
            e.stopPropagation();
            document.getElementById(entityDropDownFocusId)?.focus();
        }
    };

    const handleUtteranceEditKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        e.stopPropagation();

        switch (e.key) {
            case LuisKeyCodes.Escape:
                onDismissEditInput();
                break;
            case LuisKeyCodes.Enter:
                if (utteranceInput === utterance.text) {
                    onDismissEditInput();
                } else {
                    onSubmitEditInput(utteranceInput);
                }
                break;
            default:
        }
    };

    return (
        <Stack
            key={utterance.id}
            verticalAlign="start"
            verticalFill
            horizontal
            onKeyDown={handleKeyDown}
            onMouseEnter={() => setIsHovering(true)}
            onFocus={() => setIsHovering(true)}
            onBlur={() => setIsHovering(false)}
            onMouseLeave={() => setIsHovering(false)}>
            <Stack.Item grow>
                {isEditMode ? (
                    <LuisTextField
                        autoFocus
                        styles={editUtteranceInputStyles}
                        value={utteranceInput}
                        onChange={(_, newUtterance) => setUtteranceInput(newUtterance)}
                        onBlur={onDismissEditInput}
                        onKeyDown={handleUtteranceEditKeyDown}
                    />
                ) : (
                    <StyledLabeler
                        config={labelerConfig}
                        originalRef={labelerRef}
                        onShapeClick={canWrite ? handleShapeClick : noop}
                        onTokenHover={handleTokenHover}
                        onTokenHoverExit={handleTokenHoverExit}
                        onLabelResize={noop}
                        onSelect={handleSelect}
                        conflicts={conflicts}
                        labels={labels}
                        linesTokens={[utterance.tokenizedText]}
                        text={utterance.text}
                        data-selection-disabled
                    />
                )}
            </Stack.Item>
            {!isEditMode && (
                <UtteranceRowOptions
                    isHidden={!isHovering || !canWrite}
                    intents={intents}
                    onConfirmPrediction={() => {
                        onConfirmPrediction(utterance.id);
                    }}
                    onAddToPatterns={() => {
                        eventLoggerRef.current.log('AddToPatternsRowOption');
                        onAddToPatterns();
                    }}
                    onDelete={() => {
                        eventLoggerRef.current.log('DeleteUtteranceRowOption');
                        onDelete(utterance.id);
                    }}
                    onEdit={() => {
                        eventLoggerRef.current.log('EditUtteranceRowOption');
                        onEdit(utterance.id);
                    }}
                    isConfirmPredictionsDisabled={!labels.some(l => l.renderedAs === 'box')}
                    onReassignIntent={intentId => {
                        eventLoggerRef.current.log('ReassignIntentRowOption');
                        onReassignIntent(intentId, utterance.id);
                    }}
                    isAddAsPatternDisabled={isAddAsPatternDisabled}
                />
            )}
        </Stack>
    );
});
